import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import { LANGUAGES, language, setLanguage, t, type Language } from '../lib/i18n';

/**
 * The language switch, in the header on every screen size — never tucked in
 * the menu, because someone who cannot read the current language cannot read
 * the menu either. The button shows the current language in its own script;
 * the list shows each language in its own name, so it is findable from any of
 * them.
 */
export default function LanguageSwitcher() {
  const { user } = useApp();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const current = LANGUAGES.find((entry) => entry.code === language) ?? LANGUAGES[0]!;

  // Closes on a tap anywhere else, and on Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (code: Language) => {
    setOpen(false);
    if (code === language) return;
    // Saved on the account too, so emails arrive in the same language.
    void setLanguage(code, user ? () => api.patch('/users/me', { locale: code }) : undefined);
  };

  return (
    <div className="lang" ref={root}>
      <button
        type="button"
        className="btn btn--quiet btn--sm lang__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('Langue')}
        onClick={() => setOpen((value) => !value)}
      >
        {current.short}
      </button>
      {open ? (
        <ul className="lang__menu" role="listbox" aria-label={t('Langue')}>
          {LANGUAGES.map((entry) => (
            <li key={entry.code}>
              <button
                type="button"
                role="option"
                aria-selected={entry.code === language}
                lang={entry.code}
                dir={entry.code === 'ar' ? 'rtl' : 'ltr'}
                className={`lang__option ${entry.code === language ? 'lang__option--on' : ''}`}
                onClick={() => choose(entry.code)}
              >
                {entry.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
